package dev.lingvo.site.components.studio.fonts

import dev.lingvo.shared.screenshots.text.Brk
import dev.lingvo.shared.screenshots.text.BreakingMode
import dev.lingvo.shared.screenshots.text.RuleSegmenter
import dev.lingvo.shared.screenshots.text.Scripts
import dev.lingvo.shared.screenshots.text.Segment
import dev.lingvo.shared.screenshots.text.Segmenter
import kotlin.js.jsTypeOf

/** Browser dictionaries add native word opportunities while the common rules retain hard breaks and glue. */
class IntlSegmenter(private val forceFallback: Boolean = false) : Segmenter {
    /** Unsupported runtimes and invalid locale tags use the deterministic common fallback. */
    override fun segments(text: String, locale: String): List<Segment> {
        val fallback = RuleSegmenter.segments(text, locale)
        val mode = Scripts.rows.getValue(Scripts.scriptFor(locale, text)).breaking
        if (forceFallback || mode !in setOf(BreakingMode.SEGMENTER, BreakingMode.PHRASE_KINSOKU) && locale.substringBefore('-') != "lo") return fallback
        val ends = dictionaryEnds(text, locale) ?: return fallback
        val boundaries = mutableMapOf<Int, Brk>()
        var offset = 0
        for (segment in fallback) {
            offset += segment.text.length
            boundaries[offset] = segment.breakAfter
        }
        val glue = "\u00a0\u202f\u2060"
        for (end in ends) {
            if (end > 0 && end < text.length && text[end - 1] !in glue && text[end] !in glue &&
                text[end - 1] != '\u00ad' && text[end - 1] != '\u200d' && text[end] != '\u200d') {
                if (end !in boundaries) boundaries[end] = Brk.ALLOWED
            }
        }
        return buildList {
            var start = 0
            for ((end, kind) in boundaries.entries.sortedBy { it.key }) {
                if (end > start) add(Segment(text.substring(start, end), kind))
                start = end
            }
        }
    }
}

// JS interop stays confined here so fitting never depends on dynamic values.
private fun dictionaryEnds(text: String, locale: String): List<Int>? = try {
    val global: Any = js("globalThis")
    val intl = global.asDynamic().Intl
    if (intl == null || jsTypeOf(intl.Segmenter) != "function") null else {
        val construct: dynamic = js("(function(locale) { return new Intl.Segmenter(locale, {granularity: 'word'}); })")
        val items: Array<dynamic> = js("Array.from")(construct(locale).segment(text))
        items.map { item -> (item.index as Int) + (item.segment as String).length }
    }
} catch (_: Throwable) { null }
